import React, { useEffect, useState } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Divider,
    List,
    ListItem,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import {
    downloadDocumentBlob,
    listDocuments,
    triggerBlobDownload,
} from "../../../data/DocumentManager/documentManagerClient";

const DOCUMENT_TYPE = "staff-claim";

const StaffClaimDocumentsDrawer = ({ open, onClose, claimId }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [loading, setLoading] = useState(false);
    const [documents, setDocuments] = useState([]);

    // Preview dialog states
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState("");
    const [wordHtml, setWordHtml] = useState(null);
    const [excelHtml, setExcelHtml] = useState(null);

    /* ---------------- HELPERS ---------------- */

    const resolveFileName = (doc) => {
        if (doc.fileName) return doc.fileName;
        if (doc.originalFileName) return doc.originalFileName;
        if (doc.name) return doc.name;
        if (doc.path) return doc.path.split("/").pop();
        return `Document-${doc.id}`;
    };

    const resolveCreatedDate = (doc) => {
        const date = doc.createdDate || doc.created_date;
        return date ? new Date(date).toLocaleDateString() : "Date unavailable";
    };

    const getFileIcon = (fileName = "") => {
        if (fileName.match(/\.pdf$/i))
            return <PictureAsPdfIcon color="error" />;
        if (fileName.match(/\.(jpg|jpeg|png|gif)$/i))
            return <ImageIcon color="primary" />;
        if (fileName.match(/\.(doc|docx)$/i))
            return <DescriptionIcon color="info" />;
        return <InsertDriveFileIcon />;
    };

    const getFileExtension = (fileName) => {
        if (!fileName) return null;
        const fileNameWithoutQuery = fileName.split("?")[0];
        return fileNameWithoutQuery.split(".").pop().toLowerCase();
    };

    const getViewerType = (extension, contentType = "") => {
        const type = contentType?.toLowerCase() || "";
        const ext = extension?.toLowerCase() || "";

        if (type.includes("pdf")) return "pdf";
        if (type.includes("word") || type.includes("msword")) return "word";
        if (type.includes("excel") || type.includes("spreadsheet")) return "excel";
        if (type.includes("powerpoint") || type.includes("presentation")) return "powerpoint";
        if (type.includes("image")) return "image";
        if (type.includes("text")) return "text";

        if (["pdf"].includes(ext)) return "pdf";
        if (["doc", "docx"].includes(ext)) return "word";
        if (["xls", "xlsx"].includes(ext)) return "excel";
        if (["ppt", "pptx"].includes(ext)) return "powerpoint";
        if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(ext)) return "image";
        if (["txt", "csv", "rtf"].includes(ext)) return "text";

        return "unknown";
    };

    const canPreview = (fileName = "") =>
        fileName.match(/\.(pdf|jpg|jpeg|png|gif|doc|docx|xls|xlsx|ppt|pptx|txt)$/i);

    /* ---------------- OFFICE DOCUMENT CONVERSION ---------------- */

    const loadMammoth = () => {
        return new Promise((resolve, reject) => {
            if (window.mammoth) {
                resolve(window.mammoth);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js';
            script.onload = () => resolve(window.mammoth);
            script.onerror = () => reject(new Error('Failed to load mammoth.js'));
            document.head.appendChild(script);
        });
    };

    const loadXLSX = () => {
        return new Promise((resolve, reject) => {
            if (window.XLSX) {
                resolve(window.XLSX);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => resolve(window.XLSX);
            script.onerror = () => reject(new Error('Failed to load SheetJS'));
            document.head.appendChild(script);
        });
    };

  const convertWordToHtml = async (arrayBuffer) => {
    try {
      const mammoth = await loadMammoth();
      const result = await mammoth.convertToHtml({ 
        arrayBuffer,
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Title'] => h1.title:fresh",
          "r[style-name='Strong'] => strong",
          "r[style-name='Emphasis'] => em",
        ],
        convertImage: mammoth.images.imgElement(function(image) {
          return image.read("base64").then(function(imageBuffer) {
            return {
              src: "data:" + image.contentType + ";base64," + imageBuffer
            };
          });
        })
      });
      
      let html = result.value;
      
      // Split content into pages based on page breaks or content length
      // Word documents use section breaks and page breaks
      const pageBreakMarkers = [
        '<br style="page-break-before:always">',
        '<p style="page-break-before: always">',
        '</section>',
        '<!-- pagebreak -->'
      ];
      
      // First, try to split by actual page break markers
      let pages = [html];
      pageBreakMarkers.forEach(marker => {
        if (html.includes(marker)) {
          pages = html.split(marker);
        }
      });
      
      // If no explicit breaks found, estimate pages by content length
      // Average Word page has ~3000 characters
      if (pages.length === 1 && html.length > 3000) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const elements = Array.from(tempDiv.children);
        
        pages = [];
        let currentPage = '';
        let currentLength = 0;
        const avgPageLength = 3000;
        
        elements.forEach((element, index) => {
          const elementHtml = element.outerHTML;
          const elementLength = element.textContent.length;
          
          // Start new page if current page is getting too long
          // But keep tables together on same page
          if (currentLength > avgPageLength && element.tagName !== 'TABLE' && currentPage !== '') {
            pages.push(currentPage);
            currentPage = elementHtml;
            currentLength = elementLength;
          } else {
            currentPage += elementHtml;
            currentLength += elementLength;
          }
          
          // Push last page
          if (index === elements.length - 1 && currentPage) {
            pages.push(currentPage);
          }
        });
      }
      
      // Style each page
      const styledPages = pages.map((pageContent, index) => {
        let styledContent = pageContent;
        
        // Style tables
        styledContent = styledContent.replace(/<table>/g, '<table style="border-collapse: collapse; width: 100%; margin: 20px 0; border: 1px solid #ddd; background: white;">');
        styledContent = styledContent.replace(/<table /g, '<table style="border-collapse: collapse; width: 100%; margin: 20px 0; border: 1px solid #ddd; background: white;" ');
        
        // Style table cells
        styledContent = styledContent.replace(/<td>/g, '<td style="border: 1px solid #ddd; padding: 10px; color: #000; vertical-align: top;">');
        styledContent = styledContent.replace(/<td /g, '<td style="border: 1px solid #ddd; padding: 10px; color: #000; vertical-align: top;" ');
        
        // Style table headers
        styledContent = styledContent.replace(/<th>/g, '<th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold; color: #000; text-align: left;">');
        styledContent = styledContent.replace(/<th /g, '<th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold; color: #000; text-align: left;" ');
        
        // Style table rows
        styledContent = styledContent.replace(/<tr>/g, '<tr style="background: white;">');
        styledContent = styledContent.replace(/<tr /g, '<tr style="background: white;" ');
        
        // Style paragraphs
        styledContent = styledContent.replace(/<p>/g, '<p style="margin: 10px 0; color: #000; line-height: 1.6;">');
        styledContent = styledContent.replace(/<p /g, '<p style="margin: 10px 0; color: #000; line-height: 1.6;" ');
        
        // Style lists
        styledContent = styledContent.replace(/<ul>/g, '<ul style="margin: 10px 0; padding-left: 30px; color: #000;">');
        styledContent = styledContent.replace(/<ol>/g, '<ol style="margin: 10px 0; padding-left: 30px; color: #000;">');
        styledContent = styledContent.replace(/<li>/g, '<li style="margin: 5px 0; color: #000;">');
        
        // Style headings
        styledContent = styledContent.replace(/<h1>/g, '<h1 style="color: #000; margin: 20px 0 10px 0; font-size: 24px; font-weight: bold;">');
        styledContent = styledContent.replace(/<h2>/g, '<h2 style="color: #000; margin: 18px 0 10px 0; font-size: 20px; font-weight: bold;">');
        styledContent = styledContent.replace(/<h3>/g, '<h3 style="color: #000; margin: 16px 0 10px 0; font-size: 18px; font-weight: bold;">');
        
        // Wrap each page in a page container with page number
        return `
          <div style="
            background: white;
            padding: 60px 80px;
            margin: 20px auto;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            min-height: 800px;
            max-width: 850px;
            position: relative;
            page-break-after: always;
          ">
            <div style="
              position: absolute;
              bottom: 30px;
              right: 80px;
              color: #999;
              font-size: 12px;
              font-family: Arial, sans-serif;
            ">Page ${index + 1}</div>
            ${styledContent}
          </div>
        `;
      });
      
      return styledPages.join('\n');
    } catch (err) {
      console.error('Word conversion error:', err);
      throw new Error('Failed to convert Word document to HTML');
    }
  };

  const convertExcelToHtml = async (arrayBuffer) => {
    try {
      const XLSX = await loadXLSX();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      let html = '<div style="font-family: Arial, sans-serif;">';
      
      workbook.SheetNames.forEach((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const htmlTable = XLSX.utils.sheet_to_html(worksheet, {
          id: `sheet-${index}`,
          editable: false
        });
        
        html += `
          <div style="margin-bottom: 30px;">
            <h2 style="background: #4CAF50; color: white; padding: 10px; margin: 0;">
              ${sheetName}
            </h2>
            <div style="overflow-x: auto; border: 1px solid #ddd; background: white;">
              ${htmlTable}
            </div>
          </div>
        `;
      });
      
      html += '</div>';
      
      // Add comprehensive styling to the tables with proper text visibility
      html = html.replace(/<table/g, '<table style="border-collapse: collapse; width: 100%; font-size: 13px; background: white;"');
      html = html.replace(/<td/g, '<td style="border: 1px solid #ddd; padding: 8px; color: #000; background: white; min-width: 80px;"');
      html = html.replace(/<th/g, '<th style="border: 1px solid #ddd; padding: 8px; background: #f2f2f2; font-weight: bold; color: #000; text-align: left;"');
      html = html.replace(/<tr/g, '<tr style="background: white;"');
      
      return html;
    } catch (err) {
      console.error('Excel conversion error:', err);
      throw new Error('Failed to convert Excel document to HTML');
    }
  };

    const convertPowerPointToHtml = async () => {
        return `
      <div style="font-family: Arial, sans-serif; padding: 40px; text-align: center;">
        <div style="max-width: 600px; margin: 0 auto; background: #f5f5f5; padding: 40px; border-radius: 8px;">
          <h2 style="color: #D04423; margin-bottom: 10px;">PowerPoint Presentation</h2>
          <p style="color: #666; margin-bottom: 20px;">
            PowerPoint presentations are ready to download. Full slide preview is not available in the browser.
          </p>
          <p style="color: #999; font-size: 14px;">
            Download the file to view it in Microsoft PowerPoint, Google Slides, or compatible applications.
          </p>
        </div>
      </div>
    `;
    };

    /* ---------------- FETCH DOCUMENT ---------------- */

    const fetchDocument = async (doc) => {
        setPreviewLoading(true);
        setPreviewError(null);
        setWordHtml(null);
        setExcelHtml(null);

        const docFileName = resolveFileName(doc);
        setFileName(docFileName);

        try {
            const { blob, contentType: responseContentType } =
                await downloadDocumentBlob(DOCUMENT_TYPE, doc.id);

            // Get file extension to determine proper MIME type
            const extension = getFileExtension(docFileName);
            const detectedType = getViewerType(extension, responseContentType);
            setFileType(detectedType);

            // Create a new blob with the correct MIME type to prevent download
            let mimeType = responseContentType;
            if (detectedType === "pdf") {
                mimeType = "application/pdf";
            } else if (detectedType === "image") {
                if (extension === "png") mimeType = "image/png";
                else if (extension === "jpg" || extension === "jpeg") mimeType = "image/jpeg";
                else if (extension === "gif") mimeType = "image/gif";
                else if (extension === "svg") mimeType = "image/svg+xml";
            } else if (detectedType === "text") {
                mimeType = "text/plain";
            }

            // Create blob with correct MIME type
            const properBlob = new Blob([blob], { type: mimeType });
            const blobObjectUrl = URL.createObjectURL(properBlob);
            setBlobUrl(blobObjectUrl);

            console.log("Document loaded:", {
                fileName: docFileName,
                detectedType,
                mimeType,
                extension
            });

            // Convert Office documents to HTML
            if (detectedType === "word") {
                try {
                    const arrayBuffer = await blob.arrayBuffer();
                    const html = await convertWordToHtml(arrayBuffer);
                    setWordHtml(html);
                } catch (conversionError) {
                    console.error("Word conversion failed:", conversionError);
                }
            } else if (detectedType === "excel") {
                try {
                    const arrayBuffer = await blob.arrayBuffer();
                    const html = await convertExcelToHtml(arrayBuffer);
                    setExcelHtml(html);
                } catch (conversionError) {
                    console.error("Excel conversion failed:", conversionError);
                }
            } else if (detectedType === "powerpoint") {
                try {
                    const html = await convertPowerPointToHtml();
                    setWordHtml(html);
                } catch (conversionError) {
                    console.error("PowerPoint conversion failed:", conversionError);
                }
            }

        } catch (err) {
            setPreviewError(err.message);
            console.error("Error fetching document:", err);
        } finally {
            setPreviewLoading(false);
        }
    };

    /* ---------------- FETCH DOCUMENTS LIST ---------------- */

    const fetchDocuments = async () => {
        if (!claimId) return;

        setLoading(true);
        try {
            setDocuments(await listDocuments(`/documents/claim/${claimId}`));
        } catch {
            Swal.fire("Error", "Failed to load documents", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- PREVIEW HANDLERS ---------------- */

    const handlePreviewDocument = (doc) => {
        setCurrentDoc(doc);
        setOpenPreviewDialog(true);
        fetchDocument(doc);
    };

    const handleClosePreview = () => {
        setOpenPreviewDialog(false);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
        setCurrentDoc(null);
        setPreviewError(null);
        setFileType(null);
        setWordHtml(null);
        setExcelHtml(null);
    };

    const handleOpenInNewTab = () => {
        if (!blobUrl) return;
        window.open(blobUrl, "_blank");
    };

    const handleDownload = () => {
        if (!blobUrl) return;
        const link = document.createElement("a");
        link.href = blobUrl;
        const extension = getFileExtension(fileName) || fileType;
        link.download = fileName || `document.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadDocument = async (doc) => {
        try {
            const { blob } = await downloadDocumentBlob(DOCUMENT_TYPE, doc.id);
            triggerBlobDownload(blob, resolveFileName(doc));
        } catch (err) {
            Swal.fire("Download Failed", err.message || "Failed to download document", "error");
        }
    };

    /* ---------------- RENDER DOCUMENT VIEWER ---------------- */

    const renderDocumentViewer = () => {
        if (previewLoading) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                    <CircularProgress />
                    <Typography>Loading document...</Typography>
                </Box>
            );
        }

        if (previewError) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                    <Typography color="error" variant="h6">Document Load Error:</Typography>
                    <Alert severity="error" sx={{ width: "100%" }}>{previewError}</Alert>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => fetchDocument(currentDoc)}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        if (!currentDoc || !blobUrl) return null;

        // Handle Word documents
        if (fileType === "word") {
            if (wordHtml) {
                return (
                    <Box sx={{
                        width: "100%",
                        height: "100%",
                        overflow: "auto",
                        bgcolor: "#e5e5e5",
                        p: 2
                    }}>
                        <div
                            dangerouslySetInnerHTML={{ __html: wordHtml }}
                            style={{
                                fontFamily: 'Calibri, Arial, sans-serif',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                color: '#000'
                            }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                        <Typography variant="h6">Word Document Ready</Typography>
                        <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
                            Preview conversion failed. Please download the document to view it.
                        </Alert>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                        >
                            Download Document
                        </Button>
                    </Box>
                );
            }
        }

        // Handle Excel documents
        if (fileType === "excel") {
            if (excelHtml) {
                return (
                    <Box sx={{
                        width: "100%",
                        height: "100%",
                        overflow: "auto",
                        p: 3,
                        bgcolor: "#ffffff"
                    }}>
                        <div
                            dangerouslySetInnerHTML={{ __html: excelHtml }}
                            style={{
                                color: '#000000',
                                backgroundColor: '#ffffff'
                            }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                        <Typography variant="h6">Excel Document Ready</Typography>
                        <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
                            Preview conversion failed. Please download the document to view it.
                        </Alert>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                        >
                            Download Document
                        </Button>
                    </Box>
                );
            }
        }

        // Handle PowerPoint documents
        if (fileType === "powerpoint") {
            return (
                <Box sx={{ width: "100%", height: "100%", overflow: "auto", bgcolor: "white" }}>
                    <div dangerouslySetInnerHTML={{ __html: wordHtml }} />
                    <Box display="flex" justifyContent="center" p={3}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            sx={{ bgcolor: "#D04423", "&:hover": { bgcolor: "#B03820" } }}
                        >
                            Download Presentation
                        </Button>
                    </Box>
                </Box>
            );
        }

        // Handle PDF, images, and text files
        switch (fileType) {
            case "pdf":
                return (
                    <iframe
                        src={blobUrl}
                        type="application/pdf"
                        style={{ width: "100%", height: "100%", border: "none" }}
                        title="PDF Viewer"
                    />
                );
            case "image":
                return (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "#f5f5f5",
                            p: 2,
                        }}
                    >
                        <img
                            src={blobUrl}
                            alt="Document Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    </Box>
                );
            case "text":
                return (
                    <iframe
                        src={blobUrl}
                        style={{ width: "100%", height: "100%", border: "none" }}
                        title="Text Viewer"
                    />
                );

            default:
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                        <Typography variant="h6">Preview Not Available</Typography>
                        <Typography variant="body2" color="textSecondary">
                            This file type cannot be previewed directly in the browser.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                        >
                            Download File
                        </Button>
                    </Box>
                );
        }
    };

    /* ---------------- EFFECT ---------------- */

    useEffect(() => {
        if (open) {
            fetchDocuments();
        }
    }, [open, claimId]);

    /* ---------------- RENDER ---------------- */

    return (
        <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: 1400 }}
        PaperProps={{ sx: { width: 500, bgcolor: "#121212" } }}
      >
                {/* HEADER */}
                <Box p={2} display="flex" alignItems="center">
                    <Box flexGrow={1}>
                        <Typography variant="h6">Supporting Documents</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Staff Claim receipts & attachments
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                {/* CONTENT */}
                <Box p={2} height="100%" overflow="auto">
                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress />
                        </Box>
                    ) : documents.length === 0 ? (
                        <Typography color="text.secondary">
                            No supporting documents found.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {documents.map((doc) => {
                                const fileName = resolveFileName(doc);

                                return (
                                    <ListItem
                                        key={doc.id}
                                        sx={{
                                            mb: 1.5,
                                            borderRadius: 2,
                                            bgcolor: "rgba(255,255,255,0.04)",
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                            {getFileIcon(fileName)}

                                            <Box flexGrow={1}>
                                                <Typography noWrap>{fileName}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Uploaded on {resolveCreatedDate(doc)}
                                                </Typography>
                                            </Box>

                                            <Stack direction="row" spacing={1}>
                                                {canPreview(fileName) && (
                                                    <Button size="small" onClick={() => handlePreviewDocument(doc)}>
                                                        Preview
                                                    </Button>
                                                )}
                                                <IconButton
                                                    onClick={() => handleDownloadDocument(doc)}
                                                >
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </Drawer>

            {/* PREVIEW DIALOG */}
      <Dialog
        open={openPreviewDialog}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1500 }}
        PaperProps={{ sx: { height: "90vh", maxHeight: "90vh" } }}
      >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${colors.primary[300]}`,
                        py: 1.5,
                    }}
                >
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6">Document Viewer</Typography>
                            {fileType && (
                                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                                    ({fileType.toUpperCase()})
                                </Typography>
                            )}
                        </Box>
                        {fileName && (
                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                                {fileName}
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" gap={1} alignItems="center">
                        <IconButton
                            size="small"
                            onClick={handleOpenInNewTab}
                            disabled={!blobUrl}
                            title="Open in new tab"
                        >
                            <OpenInNewIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleDownload}
                            disabled={!blobUrl}
                            title="Download"
                        >
                            <DownloadIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleClosePreview}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0, height: "100%" }}>
                    {renderDocumentViewer()}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default StaffClaimDocumentsDrawer;
